import React, {
  createContext,
  useMemo,
  useContext,
  type ReactNode,
} from 'react';

import { Ownership } from 'lib/generated_constants/ownership';

import { useAppsignal } from 'hooks/use_appsignal';

type Appsignal = ReturnType<typeof useAppsignal>;

const OwnershipContext = createContext<{
  owner: Ownership | 'frontend';
  logError: (error: Error) => void;
} | null>(null);

type OwnershipProviderProps = {
  owner: Ownership | 'frontend';
  appsignal: Appsignal;
  children: ReactNode;
};

function fallbackErrorLogger(error: Error) {
  // oxlint-disable-next-line no-console
  console.error(error);
}

export function OwnershipProvider({
  owner,
  appsignal,
  children,
}: OwnershipProviderProps) {
  const contextValue = useMemo(
    () => ({
      owner,
      logError: appsignal ? appsignal.sendError : fallbackErrorLogger,
    }),
    [appsignal, owner],
  );

  return (
    <OwnershipContext.Provider value={contextValue}>
      {children}
    </OwnershipContext.Provider>
  );
}

export function useOwnershipContext() {
  const ownershipContext = useContext(OwnershipContext);
  const logError = useMemo(
    () => (ownershipContext ? ownershipContext.logError : () => {}),
    [ownershipContext],
  );

  if (!ownershipContext) {
    return {
      owner: 'frontend' as const,
      logError,
    };
  }

  return ownershipContext;
}
